import { useEffect, useMemo, useState } from 'react'
import { Search, Cpu } from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { aiUsageApi, type AdminAiUsageOrgRow, type OrgAiUsageReport } from '../../api/aiUsage'
import { formatNIS } from '../../utils/currency'
import Modal from '../../components/ui/Modal'

type SortKey = 'usage' | 'cost' | 'margin' | 'name'

function monthBounds(monthStr: string): { from: string; to: string } {
  const [y, m] = monthStr.split('-').map(Number)
  const from = new Date(Date.UTC(y, m - 1, 1))
  const to = new Date(Date.UTC(y, m, 1))
  return { from: from.toISOString(), to: to.toISOString() }
}

function currentMonthStr(): string {
  const now = new Date()
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`
}

function formatCredits(n: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(n)
}

function formatDay(day: string): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(day))
}

function StatCard({ title, value, helper }: { title: string; value: string; helper?: string }) {
  return (
    <div className="rounded-xl p-4" style={{ background: '#252836', border: '1px solid #2d3148' }}>
      <div className="text-xs font-medium" style={{ color: '#9ca3af' }}>{title}</div>
      <div className="mt-1.5 text-xl font-bold text-white" dir="ltr">{value}</div>
      {helper && <div className="mt-0.5 text-xs" style={{ color: '#6b7280' }}>{helper}</div>}
    </div>
  )
}

function OrgDrilldownModal({ org, from, to, onClose }: { org: AdminAiUsageOrgRow; from: string; to: string; onClose: () => void }) {
  const [report, setReport] = useState<OrgAiUsageReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    aiUsageApi.orgReport(org.organization_id, from, to)
      .then(r => { if (!cancelled) setReport(r) })
      .catch(e => { if (!cancelled) setError(e?.message ?? 'Failed to load org usage') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [org.organization_id, from, to])

  return (
    <Modal isOpen onClose={onClose} title={org.org_name} size="lg">
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-indigo-500" />
        </div>
      ) : error ? (
        <div className="text-sm text-center py-6" style={{ color: '#ef4444' }}>{error}</div>
      ) : !report || report.history.length === 0 ? (
        <div className="text-sm text-center py-6" style={{ color: '#6b7280' }}>No AI usage logged for this org in this period.</div>
      ) : (
        <div className="space-y-5">
          <div>
            <div className="text-xs font-medium mb-2" style={{ color: '#9ca3af' }}>By service</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {report.per_action.map(a => (
                <div key={a.action_code} className="rounded-lg px-3 py-2" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
                  <div className="text-xs" style={{ color: '#9ca3af' }}>{a.action_code}</div>
                  <div className="text-sm font-semibold text-white" dir="ltr">{formatCredits(a.credits)} credits</div>
                  <div className="text-xs" style={{ color: '#6b7280' }}>{a.calls} calls</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-medium mb-2" style={{ color: '#9ca3af' }}>Recent calls</div>
            <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid #2d3148' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid #2d3148' }}>
                    {['Date', 'Service', 'Model', 'User', 'Credits'].map(h => (
                      <th key={h} className="text-start px-3 py-2 font-medium text-xs" style={{ color: '#9ca3af' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.history.slice(0, 50).map((h, i) => (
                    <tr key={h.id} style={{ borderBottom: i < 49 ? '1px solid #2d3148' : undefined }}>
                      <td className="px-3 py-2 text-xs" style={{ color: '#9ca3af' }} dir="ltr">
                        {new Intl.DateTimeFormat('en-US', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(h.created_at))}
                      </td>
                      <td className="px-3 py-2 text-xs text-white">{h.action_code}</td>
                      <td className="px-3 py-2 text-xs font-mono" style={{ color: '#9ca3af' }}>{h.model}</td>
                      <td className="px-3 py-2 text-xs" style={{ color: '#9ca3af' }}>{h.user_name}</td>
                      <td className="px-3 py-2 text-xs text-white text-end" dir="ltr">{formatCredits(h.credits)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default function AiUsagePage() {
  const [month, setMonth] = useState(currentMonthStr())
  const [report, setReport] = useState<{ orgs: AdminAiUsageOrgRow[]; trend: { day: string; credits: number; cost_agorot: number }[]; totals: { credits_used: number; cost_agorot: number; topup_revenue_agorot: number; gross_margin_agorot: number; org_count: number } } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [planFilter, setPlanFilter] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('cost')
  const [selectedOrg, setSelectedOrg] = useState<AdminAiUsageOrgRow | null>(null)

  const { from, to } = useMemo(() => monthBounds(month), [month])

  const load = () => {
    setLoading(true)
    setError(null)
    aiUsageApi.adminReport(from, to)
      .then(setReport)
      .catch(e => setError(e?.message ?? 'Failed to load AI usage report'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [from, to])

  const plans = useMemo(() => {
    const set = new Set((report?.orgs ?? []).map(o => o.plan_code).filter(Boolean))
    return Array.from(set).sort()
  }, [report])

  const filtered = useMemo(() => {
    let rows = report?.orgs ?? []
    if (planFilter) rows = rows.filter(o => o.plan_code === planFilter)
    if (search) {
      const q = search.toLowerCase()
      rows = rows.filter(o => o.org_name.toLowerCase().includes(q))
    }
    const sorted = [...rows].sort((a, b) => {
      switch (sortKey) {
        case 'usage': return b.credits_used - a.credits_used
        case 'cost': return b.cost_agorot - a.cost_agorot
        case 'margin': return b.gross_margin_agorot - a.gross_margin_agorot
        case 'name': return a.org_name.localeCompare(b.org_name)
        default: return 0
      }
    })
    return sorted
  }, [report, planFilter, search, sortKey])

  const totals = report?.totals

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
    </div>
  )

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">AI Usage</h1>
          <p className="text-sm mt-0.5" style={{ color: '#9ca3af' }}>Cross-org AI credit consumption and cost, {totals?.org_count ?? 0} orgs</p>
        </div>
        <input
          type="month"
          value={month}
          onChange={e => setMonth(e.target.value)}
          className="px-3 py-2 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500"
          style={{ background: '#252836', border: '1px solid #3f4258', colorScheme: 'dark' }}
        />
      </div>

      {error ? (
        <div className="rounded-xl p-4 text-sm" style={{ background: '#ef444420', color: '#ef4444', border: '1px solid #ef444440' }}>{error}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard title="Credits used" value={formatCredits(totals?.credits_used ?? 0)} />
            <StatCard title="Provider cost" value={formatNIS(totals?.cost_agorot ?? 0)} />
            <StatCard title="Top-up revenue" value={formatNIS(totals?.topup_revenue_agorot ?? 0)} />
            <StatCard title="Gross margin" value={formatNIS(totals?.gross_margin_agorot ?? 0)} />
          </div>

          <div className="rounded-xl p-4" style={{ background: '#252836', border: '1px solid #2d3148' }}>
            <div className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Cpu size={15} /> Daily trend
            </div>
            {(report?.trend ?? []).length === 0 ? (
              <div className="text-sm text-center py-8" style={{ color: '#6b7280' }}>No usage logged this period.</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={report?.trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2d3148" />
                  <XAxis dataKey="day" tickFormatter={formatDay} stroke="#6b7280" fontSize={12} />
                  <YAxis yAxisId="credits" stroke="#6366f1" fontSize={12} />
                  <YAxis yAxisId="cost" orientation="right" stroke="#22c55e" fontSize={12} tickFormatter={(v: number) => formatNIS(v, false)} />
                  <Tooltip
                    contentStyle={{ background: '#1a1d27', border: '1px solid #2d3148', borderRadius: 8, fontSize: 12 }}
                    labelFormatter={(label: unknown) => formatDay(String(label))}
                    formatter={(value, name) => {
                      const n = Number(value ?? 0)
                      return name === 'Cost' ? [formatNIS(n), name] : [formatCredits(n), name]
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="credits" type="monotone" dataKey="credits" name="Credits" stroke="#6366f1" strokeWidth={2} dot={false} />
                  <Line yAxisId="cost" type="monotone" dataKey="cost_agorot" name="Cost" stroke="#22c55e" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px] sm:flex-none">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#6b7280' }} />
              <input
                type="text" placeholder="Search org…" value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500 w-full sm:w-56"
                style={{ background: '#252836', border: '1px solid #3f4258' }}
              />
            </div>
            <select
              value={planFilter}
              onChange={e => setPlanFilter(e.target.value)}
              className="px-3 py-2 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500"
              style={{ background: '#252836', border: '1px solid #3f4258' }}
            >
              <option value="">All plans</option>
              {plans.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              value={sortKey}
              onChange={e => setSortKey(e.target.value as SortKey)}
              className="px-3 py-2 rounded-lg text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500"
              style={{ background: '#252836', border: '1px solid #3f4258' }}
            >
              <option value="cost">Sort by cost</option>
              <option value="usage">Sort by usage</option>
              <option value="margin">Sort by margin</option>
              <option value="name">Sort by name</option>
            </select>
          </div>

          <div className="rounded-xl overflow-hidden" style={{ background: '#252836', border: '1px solid #2d3148' }}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid #2d3148' }}>
                    {['Org', 'Plan', 'Included', 'Used (plan/top-up)', '% quota', 'Top-ups', 'Cost', 'Margin', 'Last activity'].map(h => (
                      <th key={h} className="text-start px-4 py-3 font-medium text-xs whitespace-nowrap" style={{ color: '#9ca3af' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={9} className="px-6 py-8 text-center" style={{ color: '#6b7280' }}>No organizations found</td></tr>
                  ) : filtered.map((o, i) => {
                    const planUsed = Math.max(o.credits_used - o.topup_credits_remaining, 0)
                    return (
                      <tr
                        key={o.organization_id}
                        className="cursor-pointer transition-colors hover:bg-white/5"
                        style={{ borderBottom: i < filtered.length - 1 ? '1px solid #2d3148' : undefined }}
                        onClick={() => setSelectedOrg(o)}
                      >
                        <td className="px-4 py-3 text-white font-medium whitespace-nowrap">{o.org_name}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs px-2 py-1 rounded-full" style={{ color: '#a5b4fc', background: '#6366f120' }}>{o.plan_code}</span>
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#9ca3af' }} dir="ltr">{formatCredits(o.plan_included)}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#9ca3af' }} dir="ltr">
                          {formatCredits(o.credits_used)} <span style={{ color: '#6b7280' }}>({formatCredits(planUsed)} plan)</span>
                        </td>
                        <td className="px-4 py-3 text-xs" dir="ltr" style={{ color: o.quota_pct >= 90 ? '#ef4444' : o.quota_pct >= 70 ? '#f59e0b' : '#9ca3af' }}>
                          {o.quota_pct}%
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#9ca3af' }} dir="ltr">{formatNIS(o.topup_revenue_agorot)}</td>
                        <td className="px-4 py-3 text-xs text-white" dir="ltr">{formatNIS(o.cost_agorot)}</td>
                        <td className="px-4 py-3 text-xs" dir="ltr" style={{ color: o.gross_margin_agorot < 0 ? '#ef4444' : '#22c55e' }}>{formatNIS(o.gross_margin_agorot)}</td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#6b7280' }} dir="ltr">
                          {o.last_activity ? new Intl.DateTimeFormat('en-US', { dateStyle: 'short' }).format(new Date(o.last_activity)) : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                {filtered.length > 0 && totals && (
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #2d3148', background: '#1a1d27' }}>
                      <td className="px-4 py-3 text-xs font-semibold text-white" colSpan={3}>Totals</td>
                      <td className="px-4 py-3 text-xs font-semibold text-white" dir="ltr">{formatCredits(totals.credits_used)}</td>
                      <td className="px-4 py-3" />
                      <td className="px-4 py-3 text-xs font-semibold text-white" dir="ltr">{formatNIS(totals.topup_revenue_agorot)}</td>
                      <td className="px-4 py-3 text-xs font-semibold text-white" dir="ltr">{formatNIS(totals.cost_agorot)}</td>
                      <td className="px-4 py-3 text-xs font-semibold" dir="ltr" style={{ color: totals.gross_margin_agorot < 0 ? '#ef4444' : '#22c55e' }}>{formatNIS(totals.gross_margin_agorot)}</td>
                      <td className="px-4 py-3" />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}

      {selectedOrg && (
        <OrgDrilldownModal org={selectedOrg} from={from} to={to} onClose={() => setSelectedOrg(null)} />
      )}
    </div>
  )
}
